import { useEffect, useRef, useState } from 'react';

const CHECKOUT_URL = 'https://elabooks.com/checkouts/cn/hWN9vdijShZFrxH7cDKM5m5K/es-us?_r=AQABk6Gs2YiKxpZaPCLClghqTNRHI_0VO_9pIDDMnwLCZD8&auto_redirect=false&edge_redirect=true&skip_shop_pay=true';

const included = [
  { label: 'Ebook Profesional Paso a Paso', value: '$27' },
  { label: 'App Interactiva Plan de 30 Días', value: '$37' },
  { label: 'Mentor GPT Finanzas Familiares', value: '$19' },
  { label: 'Audiolibro + Formatos Digitales', value: '$14' },
];

export default function PrecioSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="precio" className="bg-emerald-950 py-20 md:py-28 px-6 md:px-12" ref={ref}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-block text-amber-400 text-xs font-bold tracking-widest uppercase mb-4">
            Oferta de Lanzamiento
          </span>
          <h2
            className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Comienza hoy tu transformación{' '}
            <span className="text-amber-400">financiera familiar</span>
          </h2>
          <p className="text-emerald-300 text-lg max-w-xl mx-auto">
            La estabilidad financiera no ocurre por casualidad. Se construye con decisiones conscientes, hábitos correctos y planificación.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start justify-center">
          {/* Pricing Card */}
          <div
            className={`w-full lg:w-[420px] flex-shrink-0 transition-all duration-700 delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="bg-white rounded-3xl p-7 md:p-8">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100">
                  <i className="ri-gift-line text-emerald-700" />
                </div>
                <span className="font-bold text-stone-800 text-sm">Sistema Completo ElaBooks</span>
              </div>

              <div className="flex flex-col gap-3 mb-7">
                {included.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-line text-emerald-600 text-sm" />
                      </div>
                      <span className="text-stone-600 text-sm">{item.label}</span>
                    </div>
                    <span className="text-stone-400 text-sm line-through">{item.value}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-stone-100 pt-5 mb-6">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-stone-500 text-sm">Valor total del sistema</span>
                  <span className="text-stone-400 text-sm font-medium line-through">$97 USD</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-900 font-bold text-lg">Precio hoy</span>
                  <div className="flex items-end gap-1">
                    <span className="text-4xl font-bold text-emerald-700" style={{ fontFamily: 'Playfair Display, serif' }}>$17</span>
                    <span className="text-stone-500 text-sm mb-1">USD</span>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2 mb-4">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-amber-600 text-sm" />
                </div>
                <span className="text-amber-800 text-xs font-semibold">Acceso inmediato después de tu compra</span>
              </div>

              <div className="flex items-center gap-3 text-stone-400 text-xs">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-stone-400" />
                </div>
                <span>Pago seguro · Descarga digital instantánea</span>
              </div>
            </div>
          </div>

          {/* CTA Card */}
          <div
            className={`flex-1 w-full transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="border border-emerald-800 rounded-3xl p-7 md:p-8 flex flex-col gap-6">
              <div>
                <h3
                  className="text-xl md:text-2xl font-bold text-white mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Un solo pago. Acceso inmediato.
                </h3>
                <p className="text-emerald-400 text-sm leading-relaxed">
                  Haz clic en el botón, completa tu compra en nuestra tienda segura y recibe acceso inmediato a todos los materiales del Sistema Completo ElaBooks.
                </p>
              </div>

              {/* Steps */}
              <div className="flex flex-col gap-3">
                {[
                  { icon: 'ri-shopping-cart-line', text: 'Haz clic en "Comprar ahora"' },
                  { icon: 'ri-bank-card-line', text: 'Completa tu pago de forma segura' },
                  { icon: 'ri-download-2-line', text: 'Descarga todos los materiales al instante' },
                  { icon: 'ri-rocket-line', text: '¡Comienza tu programa de 30 días hoy!' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-800 flex-shrink-0">
                      <i className={`${step.icon} text-amber-400 text-sm`} />
                    </div>
                    <span className="text-emerald-200 text-sm">{step.text}</span>
                  </div>
                ))}
              </div>

              <a
                href={CHECKOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap w-full bg-amber-400 hover:bg-amber-300 text-emerald-950 font-bold text-base py-4 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2 text-center"
              >
                <i className="ri-secure-payment-line" />
                Comprar ahora por $17 USD
              </a>

              <div className="flex items-center justify-center gap-6 pt-1">
                <div className="flex items-center gap-1.5 text-emerald-500 text-xs">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-lock-2-line" />
                  </div>
                  <span>Pago seguro</span>
                </div>
                <div className="flex items-center gap-1.5 text-emerald-500 text-xs">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-flashlight-line" />
                  </div>
                  <span>Acceso inmediato</span>
                </div>
                <div className="flex items-center gap-1.5 text-emerald-500 text-xs">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-download-line" />
                  </div>
                  <span>100% digital</span>
                </div>
              </div>
            </div>

            {/* Guarantee */}
            <div className="mt-5 flex items-start gap-3 px-2">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-star-line text-amber-400 text-2xl" />
              </div>
              <p className="text-emerald-400 text-xs leading-relaxed">
                <strong className="text-emerald-300">Sistema probado y estructurado.</strong> Este programa fue diseñado para familias reales con situaciones económicas reales. No necesitas ser experto en finanzas para aplicarlo.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom tagline */}
        <div
          className={`mt-16 text-center transition-all duration-700 delay-400 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p
            className="text-emerald-200 text-xl md:text-2xl font-medium leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            "Cuando las finanzas del hogar están organizadas…
            <span className="text-amber-400"> toda la familia vive con mayor tranquilidad.</span>"
          </p>
        </div>
      </div>
    </section>
  );
}
